import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DomainFormFields, DomainFormInputs, DomainsQueryKeys, useCreateDomainsMutation } from '@/modules/Domains';

import { DomainsFormView } from './DomainsFormView';
import { domainFormValidationSchema } from './validation';

export type DomainsInputMode = 'single' | 'multiple';

export const DomainsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClients = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainFormInputs>>(null);
  const [domainsMode, setDomainsMode] = useState<DomainsInputMode>('single');

  const domainRegistrarFormId = Form.useWatch<string | undefined>([DomainFormInputs.DomainRegistrarId], form);

  const { mutate: createDomainMutation, isLoading: createDomainIsLoading } = useCreateDomainsMutation()({
    onSuccess: async (data) => {
      appMessageApi.success('Домен успішно створено!');

      if (data.length === 1) navigate(AppRoutes.DomainsById.replace(':domainId', data[0].id));
      else navigate(AppRoutes.Domains);

      await queryClients.refetchQueries([DomainsQueryKeys.GetAll]);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DomainFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: DomainFormFields = {
    names: [],
    domainRegistrarAccountId: undefined,
    domainRegistrarId: undefined,
    registratedAt: undefined,
    paidAt: undefined,
  };

  const handleFinish = useCallback((values: DomainFormFields): void => {
    setFormErrors(null);
    createDomainMutation(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.Domains);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleChangeDomainsMode = useCallback((mode: 'single' | 'multiple'): void => {
    setDomainsMode(mode);
  }, []);

  return (
    <DomainsFormView
      formName="domain-create"
      formType="create"
      form={form}
      formErrors={formErrors}
      initialValues={initialValues}
      isFormLoading={createDomainIsLoading}
      validationSchema={domainFormValidationSchema}
      domainRegistrarFormId={domainRegistrarFormId}
      domainsMode={domainsMode}
      handleChangeDomainsMode={handleChangeDomainsMode}
      handleBack={handleBack}
      handleFinish={handleFinish}
      handleFinishFailed={handleFinishFailed}
    />
  );
});
